import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature } from '@openfeature/web-sdk';
import {
  getDatadogApplicationId,
  getDatadogClientToken,
  getDatadogProxyOrigin,
  getMCOEnvironment,
} from '../metaTags';
import { withOverrides } from './overridableProvider';
import type { OverridableProvider } from './overridableProvider';

export type { OverridableProvider } from './overridableProvider';
// Standard OpenFeature React surface — consumers read flags provider-agnostically
// (`useBooleanFlagValue`), so the flag vendor is contained entirely in this module.
export {
  OpenFeatureProvider,
  useBooleanFlagValue,
  useBooleanFlagDetails,
} from '@openfeature/react-sdk';

// Datadog org region — one value across all environments. Change if the org is
// not on US1 (e.g. 'us5.datadoghq.com', 'us3.datadoghq.com', 'datadoghq.eu').
const DATADOG_SITE = 'datadoghq.com';

/**
 * A credential is usable only if it's non-empty and not an unresolved secrets
 * placeholder. Deployed stacks read config from `local_config`, where a key
 * missing from the env's secrets bundle leaves the literal `<loaded from
 * secrets>` string in place — the `<` check catches that.
 */
const isUsableCred = (value: string) => !!value && !value.includes('<');

/**
 * Normalizes `MCO_ENVIRONMENT` into a stable Datadog `env` tag. Deployed values
 * carry a cluster prefix and `-mco` suffix (`gaia-dev-mco`, `prime-dev-mco`,
 * `gaia-qa-alpha`, `gaia-tokyo`); we keep the segment after the first hyphen so
 * they collapse to clean tags — `dev`, `qa`, `staging`, `sandbox`, `ci`, `prod`,
 * `tokyo` — and cluster/region variants (`gaia-`/`prime-`, east/west) share one
 * tag. Single-token values (`local`) pass through; empty falls back to `local`.
 * The Datadog dashboard only has to manage this small set of env names.
 */
const toDatadogEnv = (mcoEnv: string) => {
  if (!mcoEnv) return 'local';
  const [first, second] = mcoEnv.split('-');
  return second || first;
};

let overridableProvider: OverridableProvider | null = null;

/**
 * Registers the Datadog Feature Flags provider on OpenFeature — the single place
 * the frontend wires up feature flags. Call once at the app root, before mounting
 * `<OpenFeatureProvider>`; components then read flags via `useBooleanFlagValue`.
 *
 * @remarks
 * When Datadog creds are absent (e.g. local without config) it leaves
 * OpenFeature's default no-op provider in place, so flags resolve to their
 * fallback. Per-environment on/off is configured in the Datadog dashboard against
 * `env` (from `MCO_ENVIRONMENT`) — the frontend never compares environment.
 *
 * Whitelisting: DD's browser SDK is network-first, so ECC-safe delivery routes the
 * flag-config fetch through our own domain via `flaggingProxy` — the Amplitude
 * reverse-proxy pattern. With `DATADOG_PROXY_ORIGIN` injected (prod), the browser
 * only hits `<origin>/_datadog-flags` (already ECC-whitelisted) and an nginx route
 * forwards to `ff-cdn.datadoghq.com`; without it (devbox) the browser talks to DD
 * directly. Exposure/eval tracking is disabled so the config fetch is the only
 * browser→DD request.
 *
 * @example
 * ```tsx
 * // apps/<app>/src/index.js — mount once at the root
 * import { initDatadogFlags, OpenFeatureProvider } from '@rsos/utils/openFeature';
 *
 * initDatadogFlags();
 *
 * root.render(
 *   <OpenFeatureProvider>
 *     <App />
 *   </OpenFeatureProvider>,
 * );
 * ```
 */
export const initDatadogFlags = () => {
  const clientToken = getDatadogClientToken();
  const applicationId = getDatadogApplicationId();
  // No creds, or an unresolved `<loaded from secrets>` placeholder (a key missing
  // from the env's secrets bundle passes the literal through) → leave the default
  // no-op provider so flags fall back to defaults, rather than shipping a bogus
  // token that Datadog 401s on every config fetch.
  if (!isUsableCred(clientToken) || !isUsableCred(applicationId)) return;

  const proxyOrigin = getDatadogProxyOrigin().replace(/\/$/, '');
  const flaggingProxy = proxyOrigin
    ? `${proxyOrigin}/_datadog-flags/precompute-assignments`
    : undefined;

  const provider = new DatadogProvider({
    clientToken,
    applicationId,
    site: DATADOG_SITE,
    env: toDatadogEnv(getMCOEnvironment()),
    ...(flaggingProxy ? { flaggingProxy } : {}),
    // Keep the config fetch as the only browser→DD call (no analytics intake).
    enableExposureLogging: false,
    enableFlagEvaluationTracking: false,
    enableRumFeatureFlagTracking: false,
  });

  overridableProvider = withOverrides(provider);
  OpenFeature.setProvider(overridableProvider);
};

/**
 * Local, session-only flag override controls for the debug panel (non-prod).
 * No-ops until {@link initDatadogFlags} has registered the provider. Setting or
 * clearing an override re-renders subscribed `useBooleanFlagValue` consumers.
 */
export const setFlagOverride = (flagKey: string, value: unknown) =>
  overridableProvider?.setOverride(flagKey, value);
export const clearFlagOverride = (flagKey: string) =>
  overridableProvider?.clearOverride(flagKey);
export const clearAllFlagOverrides = () =>
  overridableProvider?.clearAllOverrides();
export const getFlagOverrides = (): Record<string, unknown> =>
  overridableProvider?.getOverrides() ?? {};
